import { ReactNode } from 'react';
import { Control, FieldPath, FieldValues, useController } from 'react-hook-form';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { FormField } from './FormField';

export interface FormSelectOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

interface FormSelectProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label?: string;
  description?: string;
  placeholder?: string;
  options: FormSelectOption[];
  disabled?: boolean;
  contentClassName?: string;
}

export const FormSelect = <T extends FieldValues>({
  name,
  control,
  label,
  description,
  placeholder,
  options,
  disabled,
  contentClassName,
}: FormSelectProps<T>) => {
  const {
    field: { value, onChange, onBlur },
    fieldState: { error },
  } = useController({ name, control });

  return (
    <FormField label={label} htmlFor={name} description={description} error={error?.message}>
      <SingleSelect.Root
        value={value === '' || value == null ? '' : String(value)}
        onValueChange={v => {
          onChange(v);
          onBlur();
        }}
        disabled={disabled}
      >
        <SingleSelect.Trigger id={name}>
          <SingleSelect.Value placeholder={placeholder ?? 'Select…'} />
        </SingleSelect.Trigger>
        <SingleSelect.Content className={contentClassName}>
          {options.map(opt => (
            <SingleSelect.Item key={opt.value} value={opt.value}>
              <SingleSelect.ItemText>
                {opt.icon ? (
                  <span className="flex items-center gap-1.5">
                    {opt.icon}
                    {opt.label}
                  </span>
                ) : (
                  opt.label
                )}
              </SingleSelect.ItemText>
            </SingleSelect.Item>
          ))}
        </SingleSelect.Content>
      </SingleSelect.Root>
    </FormField>
  );
};
